/**
 * ProcessingBlock — a full, in-flow blocking state shown while a screen's data is still being
 * computed and cannot yet be shown honestly (the modeling compute-and-store "block-until-settled"
 * UX, plan §9-SR-U). Unlike a scroll-through overlay, this is rendered IN PLACE of the real content
 * (the Modeling curves / the Portfolio loan list), so there is nothing behind it to interact with
 * or leak partial numbers from.
 *
 * A centered card fills the available height with a calm spinner, an honest title, and a supporting
 * line (typically "N loans remaining"). It is deliberately content, not a `position:fixed` scrim:
 * the screen simply mounts this instead of the data, which is the truest form of "blocked".
 *
 * i18n-free by design (a kit primitive): callers pass already-translated `title`/`description`.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { Loader } from '@mantine/core';
import styles from './ProcessingBlock.module.css';

export interface ProcessingBlockProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Headline (e.g. "Still processing"). */
  readonly title: ReactNode;
  /** Supporting line (e.g. "N loans remaining" via the count-plural i18n pattern). */
  readonly description?: ReactNode;
}

export const ProcessingBlock = forwardRef<HTMLDivElement, ProcessingBlockProps>(
  function ProcessingBlock({ title, description, className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={[styles.root, className].filter(Boolean).join(' ')}
        role="status"
        aria-live="polite"
        {...rest}
      >
        <div className={styles.card}>
          <div className={styles.spinnerRow}>
            <Loader size="md" />
          </div>
          <h2 className={styles.title}>{title}</h2>
          {description !== undefined ? <p className={styles.description}>{description}</p> : null}
        </div>
      </div>
    );
  },
);
